import { openTaskPostponement } from "../ui/task-postponement.js";
import { openRoutine } from '../ui/routines.js';
/**
 * Modal Manager
 * Handles the node properties modal (edit/create nodes).
 * Extracted from main.js for better separation of concerns.
 *
 * CRITICAL: Focus must use double requestAnimationFrame to ensure DOM is ready.
 * This pattern MUST be preserved.
 */

import { escapeHtml } from '../../../engine/core/validation.js';
import { t } from '../../../shared/i18n/index.js';
import { tagLabel } from '../../../engine/core/tag-filter.js';
import { ESTIMATE_UNITS, normalizeEstimate, formatEstimate } from "../../../engine/core/estimate.js";
import { inProgressTags, toggleTagSelection } from '../../../engine/core/node-tags.js';

// Module state
let editingId = null;
let selectedColor = null;
let propertiesEscapeHandler = null;
let modalDocId = null;
let creationOptions = {};
let modalTagDefinitions = [];

// The notes editor drags the whole TipTap/ProseMirror stack with it, so it
// lives in modal-notes-editor.js and is dynamic-imported on first modal open
// to keep it out of the boot bundle.
let NotesEd = null; // loaded module, null until first open
let notesEdLoading = null;
// Seq guard: an open→close→open race could otherwise resolve an old load and
// render stale content over the newer modal session.
let notesInitSeq = 0;
// Holds the markdown handed to initNotesEditor until the chunk arrives — a
// save before then returns it unchanged (the user can't have edited yet).
let pendingNotesMarkdown = '';

function loadNotesEditor() {
  notesEdLoading ||= import('./modal-notes-editor.js').then((m) => (NotesEd = m));
  return notesEdLoading;
}

// Stored callbacks
let callbacks = null;

// Node color palettes (interfaces/classic/ui/theme.js COLOR_THEMES): translated names by key.
const PALETTES = ['vibrant', 'pastel', 'earth', 'ocean', 'sunset', 'mono'];
function paletteName(key, fallback) {
  return PALETTES.includes(key) ? t(`app.palette.${key}`) : fallback;
}

// Tag state for current modal session
let modalNodeTags = [];

/**
 * Render tag pills in the modal (toggle on click, no checkboxes).
 * Tags use {tagId, setAt} format. modalNodeTags stores tag IDs for the current session.
 */
function renderModalTags(node, parentId, selectedIds) {
  const container = document.getElementById('f-tags');
  if (!container) return;

  // Pass the node id so the picker pulls from the node's host doc — tags
  // are per-doc and a linked-node edit should show its own doc's tags.
  const tagDefs = callbacks.getTagDefinitions?.(node?.id || parentId) || [];
  modalTagDefinitions = tagDefs;
  modalNodeTags = selectedIds || (node ? node.tags || [] : inProgressTags(tagDefs))
    .map(tag => typeof tag === 'string' ? tag : tag.tagId);
  const allBtns = [];

  container.innerHTML = '';
  for (const tag of tagDefs) {
    const has = modalNodeTags.includes(tag.id);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'modal-tag-pill' + (has ? ' active' : '');
    btn.textContent = tagLabel(tag);
    btn.dataset.tagId = tag.id;
    btn.setAttribute('aria-pressed', String(has));
    btn.addEventListener('click', () => {
      modalNodeTags = toggleTagSelection(modalNodeTags, tag.id, tagDefs);
      allBtns.forEach(b => {
        const active = modalNodeTags.includes(b.dataset.tagId);
        b.classList.toggle('active', active);
        b.setAttribute('aria-pressed', String(active));
      });
    });
    allBtns.push(btn);
    container.appendChild(btn);
  }

  if (tagDefs.length === 0) {
    container.innerHTML = `<span style="color:var(--text-muted);font-size:12px">${escapeHtml(t('app.nodeModal.noTags'))}</span>`;
  }
}

/**
 * Setup modal event handlers.
 *
 * @param {Object} cbs - Callbacks object with dependencies:
 *   - getState: () => AppState
 *   - get: (key) => any
 *   - findNode: (id) => Node
 *   - findParent: (id) => Node
 *   - getAll: () => Node[]
 *   - getPath: (id) => Node[]
 *   - updateNode: (id, updates) => void
 *   - addNode: (parentId, node) => void
 *   - moveNode: (id, newParentId, index) => void
 *   - setSelected: (id) => void
 *   - switchView: (viewName) => void
 *   - showModalGeneric: (modalId) => void
 *   - closeModalGeneric: (modalId) => void
 *   - generateId: () => string
 *   - getThemeApi: () => ThemeApi|null
 *   - getColors: () => string[]
 *   - beginBatch: () => void
 *   - endBatch: () => void
 */
// Estimate control state: { value, unit } or null (None)
let modalEstimate = null;

function renderEstimateUI() {
  const slider = document.getElementById("f-estimate-value");
  const readout = document.getElementById("f-estimate-readout");
  const pills = document.querySelectorAll("#f-estimate .estimate-unit");
  if (!slider || !readout) return;
  const unit = modalEstimate?.unit || "";
  pills.forEach(b => {
    const active = (b.dataset.unit || '') === unit;
    b.classList.toggle('active', active);
    b.setAttribute('aria-pressed', String(active));
  });
  if (!modalEstimate) {
    slider.hidden = true;
    readout.textContent = "";
    return;
  }
  const spec = ESTIMATE_UNITS[modalEstimate.unit];
  slider.hidden = false;
  slider.min = String(spec.min);
  // Keep values entered elsewhere (import) reachable even if beyond the slider's usual range.
  slider.max = String(Math.max(spec.max, modalEstimate.value));
  slider.value = String(modalEstimate.value);
  readout.textContent = formatEstimate(modalEstimate);
  slider.setAttribute('aria-valuetext', readout.textContent);
  const ticks = document.getElementById('f-estimate-ticks');
  if (ticks) ticks.innerHTML = Array.from({length: Math.min(12, Number(slider.max))}, (_, i) => `<option value="${i + 1}"></option>`).join('');
}

function setupEstimateControl() {
  const field = document.getElementById("f-estimate");
  const slider = document.getElementById("f-estimate-value");
  if (!field || !slider || field.dataset.wired) return;
  field.dataset.wired = "1";
  field.querySelectorAll(".estimate-unit").forEach((btn) => {
    btn.addEventListener("click", () => {
      const unit = btn.dataset.unit || "";
      if (!unit) {
        modalEstimate = null;
      } else if (modalEstimate?.unit !== unit) {
        // Switching unit keeps a sensible value: clamp into the new range.
        const spec = ESTIMATE_UNITS[unit];
        const value = Math.min(Math.max(modalEstimate?.value || 1, spec.min), spec.max);
        modalEstimate = { value, unit };
      }
      renderEstimateUI();
    });
  });
  slider.addEventListener("input", () => {
    if (!modalEstimate) return;
    modalEstimate = { ...modalEstimate, value: Number(slider.value) };
    renderEstimateUI();
  });
}

export function setupModal(cbs) {
  callbacks = cbs;
  setupEstimateControl();

  const modal = document.getElementById("modal");
  const modalCancel = document.getElementById("modal-cancel");
  const modalSave = document.getElementById("modal-save");

  if (!modal) return;

  if (!propertiesEscapeHandler) {
    propertiesEscapeHandler = (e) => {
      if (e.key !== "Escape") return;
      if (!modal.classList.contains("active")) return;
      e.preventDefault();
      e.stopPropagation();
      closePropertiesModal();
    };
    document.addEventListener("keydown", propertiesEscapeHandler);
  }

  // Close modal on cancel or background click
  modalCancel?.addEventListener("click", closePropertiesModal);
  modal?.addEventListener("click", (e) => {
    if (e.target === modal) closePropertiesModal();
  });

  // Save button
  modalSave?.addEventListener("click", saveModal);
  document.getElementById('f-name')?.addEventListener('keydown', e => {
    if (!editingId && e.key === 'Enter' && !e.isComposing) {
      e.preventDefault();
      saveModal();
    }
  });
  document.getElementById('node-create-more')?.addEventListener('click', e => {
    const expanded = modal.classList.toggle('creation-expanded');
    e.currentTarget.setAttribute('aria-expanded', String(expanded));
    e.currentTarget.textContent = expanded ? t('app.nodeModal.fewerOptions') : t('app.nodeModal.moreOptions');
  });

}

/**
 * Open the node properties modal.
 * @param {string|null} nodeId - Node ID to edit, or null for new node
 */
export function openModal(nodeId = null, options = {}) {
  editingId = nodeId;
  modalDocId = callbacks.get('currentDocId');
  creationOptions = options;
  const node = nodeId ? callbacks.findNode(nodeId) : null;
  const modal = document.getElementById("modal");
  modal.classList.toggle('node-create-modal', !node);
  modal.classList.remove('creation-expanded');
  document.getElementById('node-appearance').open = false;
  const more = document.getElementById('node-create-more');
  more.hidden = !!node;
  more.textContent = t('app.nodeModal.moreOptions');
  more.setAttribute('aria-expanded', 'false');
  document.getElementById('modal-save').textContent = node ? t('common.save') : t('app.nodeModal.add');

  document.getElementById("modal-title").textContent = node
    ? t("app.nodeModal.title")
    : t("app.nodeModal.add");
  document.getElementById("f-name").value = node?.name || options.name || "";

  // Parent selector
  const parentSel = document.getElementById("f-parent");
  const allNodes = callbacks.getAll();
  const currentParent = node
    ? callbacks.findParent(node.id)
    : callbacks.findNode(options.parentId || callbacks.get("focusId"));

  parentSel.innerHTML = allNodes
    .map((n) => {
      const depth = callbacks.getPath(n.id).length - 1;
      const selected =
        currentParent?.id === n.id;
      return `<option value="${escapeHtml(n.id)}" ${selected ? "selected" : ""}>${"—".repeat(depth)} ${escapeHtml(n.name || t("app.nodeModal.root"))}</option>`;
    })
    .join("");

  // Color picker with theme support
  const themeApi = callbacks.getThemeApi();
  const colors = themeApi?.getCurrentColors?.() || callbacks.getColors();
  const currentTheme = themeApi?.getColorTheme?.() || null;
  selectedColor = node?.color || options.color || currentParent?.color || colors[0];

  // Render theme selector buttons
  const themeSelector = document.getElementById("f-color-themes");
  if (themeSelector && themeApi?.COLOR_THEMES) {
    themeSelector.innerHTML = Object.keys(themeApi.COLOR_THEMES)
      .map(
        (key) =>
          `<button type="button" class="color-theme-btn ${key === currentTheme ? "active" : ""}" data-theme="${key}">${escapeHtml(paletteName(key, themeApi.COLOR_THEMES[key].name))}</button>`
      )
      .join("");
  } else if (themeSelector) {
    themeSelector.innerHTML = "";
  }

  // Render color swatches
  const colorPicker = document.getElementById("f-colors");
  const renderColorSwatches = () => {
    if (!colorPicker) return;
    const themeColors = themeApi?.getCurrentColors?.() || callbacks.getColors();
    const safeColorRe = /^#[0-9A-Fa-f]{3,8}$/;
    colorPicker.innerHTML = themeColors
      .filter((c) => safeColorRe.test(c))
      .map(
        (c) =>
          `<div class="color-opt ${c.toLowerCase() === selectedColor?.toLowerCase() ? "selected" : ""}" data-color="${c}" style="background:${c}"></div>`
      )
      .join("");

    // Attach click handlers to swatches
    colorPicker.querySelectorAll(".color-opt").forEach((opt) => {
      opt.onclick = () => {
        colorPicker
          .querySelectorAll(".color-opt")
          .forEach((o) => o.classList.remove("selected"));
        opt.classList.add("selected");
        selectedColor = opt.dataset.color;
        // Sync custom color inputs
        const picker = document.getElementById("f-color-picker");
        const swatch = document.getElementById("f-color-swatch");
        const hex = document.getElementById("f-color-hex");
        if (picker) picker.value = selectedColor;
        if (swatch) swatch.style.background = selectedColor;
        if (hex) hex.value = selectedColor;
      };
    });
  };
  renderColorSwatches();

  // Theme button click handlers
  if (themeSelector && themeApi?.setColorTheme) {
    themeSelector.querySelectorAll(".color-theme-btn").forEach((btn) => {
      btn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        themeApi.setColorTheme(btn.dataset.theme);
        themeSelector
          .querySelectorAll(".color-theme-btn")
          .forEach((b) => b.classList.remove("active"));
        btn.classList.add("active");
        renderColorSwatches();
      };
    });
  }

  // Custom color inputs (picker + hex)
  const colorPickerInput = document.getElementById("f-color-picker");
  const colorSwatch = document.getElementById("f-color-swatch");
  const hexInput = document.getElementById("f-color-hex");

  // Helper to update all custom color UI elements
  const updateCustomColorUI = (color) => {
    if (colorPickerInput) colorPickerInput.value = color;
    if (colorSwatch) colorSwatch.style.background = color;
    if (hexInput) hexInput.value = color;
  };

  // Initialize custom color UI
  updateCustomColorUI(selectedColor);

  // Color picker (wheel) input
  if (colorPickerInput) {
    colorPickerInput.oninput = (e) => {
      selectedColor = e.target.value;
      updateCustomColorUI(selectedColor);
      // Deselect swatches when using custom color
      colorPicker
        ?.querySelectorAll(".color-opt")
        .forEach((o) => o.classList.remove("selected"));
    };
  }

  // Hex text input
  if (hexInput) {
    hexInput.oninput = (e) => {
      let value = e.target.value.trim();
      // Add # if missing
      if (value && !value.startsWith("#")) {
        value = "#" + value;
        hexInput.value = value;
      }
      // Validate hex format
      if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
        selectedColor = value;
        if (colorPickerInput) colorPickerInput.value = value;
        if (colorSwatch) colorSwatch.style.background = value;
        // Deselect swatches when using custom color
        colorPicker
          ?.querySelectorAll(".color-opt")
          .forEach((o) => o.classList.remove("selected"));
      }
    };
  }

  // Reset to parent color button
  const resetBtn = document.getElementById("f-color-reset");
  if (resetBtn) {
    // Only show if node has a parent (not root)
    const parentColor = currentParent?.color;
    resetBtn.style.display = parentColor ? "" : "none";
    resetBtn.onclick = (e) => {
      e.preventDefault();
      if (parentColor) {
        selectedColor = parentColor;
        updateCustomColorUI(selectedColor);
        renderColorSwatches();
      }
    };
  }

  // Dates are planning information and stay empty until the user sets them.
  // (They used to default to today / today + 7, which put every node on the
  // Gantt with a fake one-week window and made "overdue" meaningless.)
  document.getElementById("f-start").value = node?.startDate || "";
  document.getElementById("f-end").value = node?.endDate || "";
  modalEstimate = normalizeEstimate(node?.estimate);
  renderEstimateUI();
  // Notes: TipTap editor with markdown
  // node.notes can be a string (markdown) or array (legacy) — normalize to string
  const notesStr = typeof node?.notes === 'string' ? node.notes : '';
  initNotesEditor(notesStr);

  // Tags
  renderModalTags(node, currentParent?.id);
  parentSel.onchange = () => {
    if (node) return;
    // Tags belong to the parent's document. Retain equivalent choices when
    // the user changes to a parent in a linked document.
    const nextDefs = callbacks.getTagDefinitions?.(parentSel.value) || [];
    const nextIds = modalNodeTags.map(id => {
      const old = modalTagDefinitions.find(tag => tag.id === id);
      return nextDefs.find(tag => tag.id === id || (old && tag.name === old.name))?.id;
    }).filter(Boolean);
    renderModalTags(null, parentSel.value, nextIds);
  };
  const taskActions = document.getElementById('node-task-actions');
  taskActions.replaceChildren();
  if (node?.id && !node._sourceDocId && !callbacks.isReadOnly?.()) {
    const routineButton=document.createElement('button');
    routineButton.id='configure-routine';routineButton.type='button';routineButton.className='btn btn-secondary';
    routineButton.textContent=t('app.nodeModal.routine');
    routineButton.title=t('app.nodeModal.routineHint');
    routineButton.onclick=()=>{const id=editingId;saveModal();if(!editingId)openRoutine(id,true);};
    taskActions.append(routineButton);
    if (callbacks.canPostponeTask?.(node.id)) {
      const postponeButton = document.createElement('button');
      postponeButton.id = 'postpone-task'; postponeButton.type = 'button'; postponeButton.className = 'btn btn-secondary';
      postponeButton.title = t('app.nodeModal.postponeHint');
      postponeButton.onclick = () => { const id = editingId; saveModal(); if (!editingId) openTaskPostponement(id); };
      taskActions.prepend(postponeButton);
    }
  }

  taskActions.hidden = !taskActions.childElementCount;
  refreshTaskPostponement();

  // Use unified modal system (handles focus trap, Escape key)
  callbacks.showModalGeneric("modal");

  // CRITICAL: Focus must use double requestAnimationFrame
  // This ensures DOM is ready after modal's own rAF completes
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const nameInput = document.getElementById("f-name");
      nameInput?.focus();
      nameInput?.select();
    });
  });
}

/** Refresh scheduling status without replacing any unsaved form fields. */
export function refreshTaskPostponement() {
  const status = document.getElementById('node-task-postponement');
  if (!status) return;
  const until = editingId && modalDocId === callbacks?.get('currentDocId')
    ? callbacks.getTaskPostponement?.(editingId)?.until : null;
  status.hidden = !until;
  status.textContent = until ? t('app.nodeModal.postponedUntil', { date: until }) : '';
  const button = document.getElementById('postpone-task');
  if (button) button.textContent = until ? t('app.nodeModal.changeReturnDate') : t('app.nodeModal.postpone');
}

/**
 * Close the properties modal without saving.
 */
export function closePropertiesModal() {
  destroyNotesEditor();
  callbacks.closeModalGeneric("modal");
  editingId = null;
}

/**
 * Save the modal form and close.
 */
export function saveModal() {
  if (modalDocId !== callbacks.get('currentDocId') || callbacks.isReadOnly?.()) {
    callbacks.showToast?.(t('app.nodeModal.notEditable'), {type: 'warning'});
    return;
  }

  const name = document.getElementById("f-name").value.trim();
  if (!name) return;

  const parentId = document.getElementById("f-parent").value;
  const startDate = document.getElementById("f-start").value || null;
  const endDate = document.getElementById("f-end").value || null;
  const estimate = normalizeEstimate(modalEstimate);
  const notes = getNotesMarkdown();

  if (editingId) {
    // Update existing node
    const node = callbacks.findNode(editingId);
    if (!node) {
      closePropertiesModal();
      return;
    }


    // In collab mode, notes are synced via Y.XmlFragment — don't include stale string
    const collabEnabled = callbacks.isCollabNotesEnabled?.();
    // Build tags array with timestamps (preserve existing timestamps, add new ones)
    const existingTags = node.tags || [];
    const newTags = modalNodeTags.length > 0
      ? modalNodeTags.map(tagId => {
          const existing = existingTags.find(tag =>
            typeof tag === 'string' ? tag === tagId : tag.tagId === tagId
          );
          if (existing && typeof existing === 'object') return existing;
          return { tagId, setAt: new Date().toISOString() };
        })
      : null;
    const updates = collabEnabled
      ? { name, startDate, endDate, estimate, tags: newTags }
      : { name, startDate, endDate, estimate, notes, tags: newTags };

    // Update color if changed
    if (selectedColor !== node.color) {
      updates.color = selectedColor;
      const applyToChildren =
        document.getElementById("f-color-apply-children")?.checked ?? true;
      if (applyToChildren) {
        // Apply color to all descendants (batched for CRDT efficiency)
        callbacks.beginBatch();
        try {
          const applyColorToTree = (n, color) => {
            callbacks.updateNode(n.id, { color });
            if (n.children) {
              n.children.forEach((child) => applyColorToTree(child, color));
            }
          };
          applyColorToTree(node, selectedColor);
        } finally {
          callbacks.endBatch();
        }
      }
    }

    callbacks.updateNode(editingId, updates);

    // Handle parent change
    const currentParent = callbacks.findParent(editingId);
    if (currentParent?.id !== parentId) {
      callbacks.moveNode(editingId, parentId, -1);
    }
  } else {
    // Create new node
    const newNodeTags = modalNodeTags.length > 0
      ? modalNodeTags.map(tagId => ({ tagId, setAt: new Date().toISOString() }))
      : null;
    const newNode = {
      id: callbacks.generateId(),
      name,
      color: selectedColor,
      startDate,
      endDate,
      estimate,
      notes,
      tags: newNodeTags,
      children: [],
    };
    if (!callbacks.findNode(parentId)) {
      callbacks.showToast?.(t('app.nodeModal.chooseParent'), {type: 'warning'});
      return;
    }
    try {
      if (!callbacks.addNode(parentId, newNode)) {
        callbacks.showToast?.(t('app.nodeModal.addFailed'), {type: 'warning'});
        return;
      }
    } catch (error) {
      callbacks.showToast?.(error.message || t('app.nodeModal.addError'), {type: 'error'});
      return;
    }
    creationOptions.onCreated?.(newNode, parentId);
    callbacks.setSelected(newNode.id);
  }

  closePropertiesModal();

  // Re-render current view
  const currentView = callbacks.get("currentView");
  callbacks.switchView(currentView);
}

/**
 * Get current editing node ID (for external use).
 */
export function getEditingId() {
  return editingId;
}

/**
 * Initialize the notes area (view mode, click to edit).
 * The editor module loads lazily; until it arrives the area stays blank and a
 * save returns the original markdown unchanged.
 * @param {string} markdown - Initial markdown content
 */
function initNotesEditor(markdown) {
  pendingNotesMarkdown = markdown || '';
  const seq = ++notesInitSeq;
  loadNotesEditor().then(() => {
    if (seq !== notesInitSeq) return; // a newer session (or close) superseded this open
    NotesEd.init(pendingNotesMarkdown, {
      callbacks,
      getEditingId: () => editingId,
    });
  });
}

/**
 * Get the notes content as markdown.
 * @returns {string}
 */
function getNotesMarkdown() {
  return NotesEd ? NotesEd.getMarkdown() : pendingNotesMarkdown;
}

/**
 * Destroy the notes editor and clean up.
 */
function destroyNotesEditor() {
  ++notesInitSeq; // cancel any in-flight lazy init
  pendingNotesMarkdown = '';
  NotesEd?.destroy();
}
